/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { notifyError } from "../../utils/bugsnag";
import FlyerRender from "./FlyerRender";
import { flyerDescriptionHtml, flyerFontSizePx, paperPixels, renderFlyerFiles } from "./flyerBuilder";
import { attachFlyerPdf } from "./setupChain";
import { publicFormUrl } from "./api";
import { useStartData } from "./StartContext";
import { localImageUrl } from "./imaging";
import { flyerColors } from "./flyerBuilder";

// Renders the flyer PDF on the phone and attaches it to the flyer record. Lives
// above the steps (not inside one screen) so a render started on Customize
// keeps going while the person moves on to Try it. The render node is mounted
// off screen only while a job runs.
//
// Callers that have just saved the store, form or flyer can pass them in:
// the shared queries refresh a moment later, and the render must not wait.

const FlyerJobContext = createContext(null);

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

export function FlyerJobProvider({ children }) {
  const { t } = useLanguage();
  const context = useStartData();
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState("idle");
  const [files, setFiles] = useState(null);
  const nodeRef = useRef(null);
  const inFlight = useRef(null);
  // One render node at a time: a second render queues behind the first
  // instead of replacing it (which would leave the first promise unsettled).
  const renderQueue = useRef(Promise.resolve());

  const colors = useMemo(() => flyerColors(context.store), [context.store]);

  // Runs the mounted render for the current job.
  useEffect(() => {
    if (!job) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await nextFrame();
        const rendered = await renderFlyerFiles(nodeRef.current, job.paperSize, job.colors.backgroundColor);
        if (!cancelled) job.resolve(rendered);
      } catch (error) {
        if (!cancelled) job.reject(error);
      } finally {
        if (job.ownsLogo && job.logoUrl?.startsWith("blob:")) URL.revokeObjectURL(job.logoUrl);
        if (!cancelled) setJob(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [job]);

  // Renders the flyer and returns { pdf, jpeg } without saving anything.
  const renderFiles = useCallback(
    async ({ paperSize, logoUrl, form, store } = {}) => {
      const targetForm = form || context.initForm;
      const targetStore = store || context.store;
      if (!targetForm) throw new Error("No sign-up form yet");
      const size = paperSize || context.flyer?.paper_size || "a4";
      const run = renderQueue.current.then(async () => {
        const local = logoUrl || (await localImageUrl(targetStore?.logo_url));
        return new Promise((resolve, reject) => {
          setJob({
            paperSize: size,
            logoUrl: local,
            ownsLogo: !logoUrl,
            form: targetForm,
            store: targetStore,
            colors: flyerColors(targetStore),
            resolve,
            reject,
          });
        });
      });
      renderQueue.current = run.catch(() => {});
      return run;
    },
    [context.flyer?.paper_size, context.initForm, context.store],
  );

  // Makes sure the flyer has a PDF, or re-renders it when `force` (the paper
  // size or the store's look changed). A plain "make sure" joins a run that is
  // already going; a forced or resized one queues behind it, so the PDF that
  // ends up attached is always the newest look.
  const ensurePdf = useCallback(
    async ({ force = false, paperSize, logoUrl, flyer, form, store } = {}) => {
      const targetFlyer = flyer || context.flyer;
      const targetForm = form || context.initForm;
      const targetStore = store || context.store;
      const storeId = targetStore?.id || context.storeId;
      if (!targetFlyer || !targetForm || !storeId) return null;
      if (!force && targetFlyer.pdf_url && (!paperSize || paperSize === targetFlyer.paper_size)) return null;
      if (inFlight.current && !force && !paperSize) return inFlight.current;
      const size = paperSize || targetFlyer.paper_size || "a4";
      const run = async () => {
        setStatus("working");
        try {
          const rendered = await renderFiles({ paperSize: size, logoUrl, form: targetForm, store: targetStore });
          await attachFlyerPdf({
            storeId,
            flyer: targetFlyer,
            formId: targetForm.id,
            paperSize: size,
            colors: flyerColors(targetStore),
            t,
            pdf: rendered.pdf,
          });
          setFiles({ ...rendered, paperSize: size });
          setStatus("done");
          context.refresh();
          return rendered;
        } catch (error) {
          setStatus("error");
          notifyError(error, (event) => {
            event.context = "start.flyerPdf";
          });
          throw error;
        }
      };
      const previous = inFlight.current || Promise.resolve();
      const current = previous.catch(() => {}).then(run);
      inFlight.current = current;
      current
        .finally(() => {
          if (inFlight.current === current) inFlight.current = null;
        })
        .catch(() => {});
      return current;
    },
    [context, renderFiles, t],
  );

  const value = useMemo(
    () => ({ ensurePdf, renderFiles, status, files, colors }),
    [ensurePdf, renderFiles, status, files, colors],
  );

  const pixels = job ? paperPixels(job.paperSize) : null;

  return (
    <FlyerJobContext.Provider value={value}>
      {children}
      {job && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            width: `${pixels.width}px`,
            height: `${pixels.height}px`,
            pointerEvents: "none",
          }}
        >
          <FlyerRender
            ref={nodeRef}
            logoUrl={job.logoUrl}
            headerText={t("flyers.new.form.defaultHeaderText")}
            formUrl={publicFormUrl(job.form.uuid)}
            descriptionHtml={flyerDescriptionHtml(t)}
            backgroundColor={job.colors.backgroundColor}
            textColor={job.colors.textColor}
            showFooter={job.store?.current_plan !== "advanced"}
            fontSize={`${flyerFontSizePx(job.paperSize)}px`}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      )}
    </FlyerJobContext.Provider>
  );
}

export function useFlyerJob() {
  const value = useContext(FlyerJobContext);
  if (!value) throw new Error("useFlyerJob must be used inside FlyerJobProvider");
  return value;
}
